import { ClientType, TaxIdStatus, VatStatus } from './enums';
import { isSpanishTerritory } from './vat';

/**
 * Spanish tax numbers — NIF (persons), NIE (foreign residents), CIF (legal entities) —
 * validated by their check digit. Anything else is "not Spanish" and is validated
 * elsewhere (VIES for EU VAT numbers; national numbers outside the EU are accepted as given).
 */
export type SpanishTaxIdKind = 'NIF' | 'NIE' | 'CIF';

export interface SpanishTaxId {
  readonly kind: SpanishTaxIdKind | null;
  /** True only when the format AND the check digit are correct. */
  readonly valid: boolean;
  /** Uppercase, no separators. */
  readonly normalized: string;
}

const NIF_LETTERS = 'TRWAGMYFPDXBNJZSQVHLCKE';
const CIF_LETTERS = 'JABCDEFGHI';

/** Uppercase and strip spaces, dots and dashes. */
export function normalizeTaxId(value: string | null | undefined): string {
  return (value ?? '').toUpperCase().replace(/[\s.\-]/g, '');
}

export function classifySpanishTaxId(value: string | null | undefined): SpanishTaxId {
  const v = normalizeTaxId(value);
  if (/^\d{8}[A-Z]$/.test(v)) {
    const expected = NIF_LETTERS[Number(v.slice(0, 8)) % 23];
    return { kind: 'NIF', valid: v[8] === expected, normalized: v };
  }
  if (/^[XYZ]\d{7}[A-Z]$/.test(v)) {
    const digits = { X: '0', Y: '1', Z: '2' }[v[0] as 'X' | 'Y' | 'Z'] + v.slice(1, 8);
    const expected = NIF_LETTERS[Number(digits) % 23];
    return { kind: 'NIE', valid: v[8] === expected, normalized: v };
  }
  if (/^[ABCDEFGHJKLMNPQRSUVW]\d{7}[0-9A-J]$/.test(v)) {
    const digits = v.slice(1, 8);
    let sum = 0;
    for (let i = 0; i < 7; i += 1) {
      const d = Number(digits[i]);
      if (i % 2 === 0) {
        const doubled = d * 2;
        sum += Math.floor(doubled / 10) + (doubled % 10);
      } else {
        sum += d;
      }
    }
    const control = (10 - (sum % 10)) % 10;
    const letterOnly = 'KPQSNWR'.includes(v[0]);
    const digitOnly = 'ABEH'.includes(v[0]);
    const last = v[8];
    const okDigit = last === String(control);
    const okLetter = last === CIF_LETTERS[control];
    const valid = letterOnly ? okLetter : digitOnly ? okDigit : okDigit || okLetter;
    return { kind: 'CIF', valid, normalized: v };
  }
  return { kind: null, valid: false, normalized: v };
}

/** True when the client type agrees with a Spanish tax number (CIF → company, NIF / NIE → person). */
export function taxIdMatchesType(kind: SpanishTaxIdKind | null, type: ClientType): boolean {
  if (kind === null) return true;
  return kind === 'CIF' ? type === ClientType.COMPANY : type === ClientType.PERSON;
}

/**
 * Status of a billing entity's tax number, derived from the number, the billing country
 * and (for EU countries other than Spain) the last VIES result.
 */
export function taxIdStatusFor(input: {
  cifVat: string | null | undefined;
  countryCode: string | null | undefined;
  vatNumber?: string | null;
  vatStatus?: VatStatus | null;
}): TaxIdStatus {
  const cc = (input.countryCode ?? '').trim().toUpperCase();
  const number = normalizeTaxId(input.cifVat);
  if (cc === '' || isSpanishTerritory(cc)) {
    if (!number) return TaxIdStatus.MISSING;
    return classifySpanishTaxId(number).valid ? TaxIdStatus.VALID : TaxIdStatus.INVALID;
  }
  if (EU_COUNTRIES.has(cc)) {
    // Other EU country: the VAT number is the tax number and VIES is the authority.
    if (!normalizeTaxId(input.vatNumber) && !number) return TaxIdStatus.MISSING;
    switch (input.vatStatus) {
      case VatStatus.VALID:
        return TaxIdStatus.VALID;
      case VatStatus.INVALID:
        return TaxIdStatus.INVALID;
      default:
        return TaxIdStatus.UNVERIFIED;
    }
  }
  // Outside the EU: a national number cannot be checked — accepted as given.
  return number ? TaxIdStatus.VALID : TaxIdStatus.MISSING;
}

/** National (Spain) vs international, derived from the billing country. */
export function nationalOrInternational(countryCode: string | null | undefined): 'NATIONAL' | 'INTERNATIONAL' {
  return isSpanishTerritory(countryCode) ? 'NATIONAL' : 'INTERNATIONAL';
}

const EU_COUNTRIES = new Set([
  'AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'EL', 'GR', 'ES', 'FI', 'FR', 'HR', 'HU',
  'IE', 'IT', 'LT', 'LU', 'LV', 'MT', 'NL', 'PL', 'PT', 'RO', 'SE', 'SI', 'SK', 'XI',
]);
